import { VALIDATION_MESSAGES } from '../constants/messages';

// Email validation
export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  if (!email || !email.trim()) {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim();

  // RFC 5322 compliant email regex (simplified but comprehensive)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  if (trimmedEmail.length > 254) {
    return { isValid: false, error: 'Email address is too long (max 254 characters)' };
  }

  // Check for common typos
  const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
  const domain = trimmedEmail.split('@')[1]?.toLowerCase();
  const suspiciousDomains = ['gmial.com', 'gmai.com', 'yahooo.com', 'hotmial.com'];

  if (domain && suspiciousDomains.includes(domain)) {
    const suggested = commonDomains.find(d => d.includes(domain.substring(0, 4)));
    return {
      isValid: false,
      error: suggested ? `Did you mean ${suggested}?` : 'Please check the email domain'
    };
  }

  return { isValid: true };
};

// Which of the password composition rules the entered value satisfies
export interface PasswordStrength {
  readonly minLength: boolean;
  readonly hasUppercase: boolean;
  readonly hasLowercase: boolean;
  readonly hasNumber: boolean;
  readonly hasSpecialChar: boolean;
}

// Password validation with strength requirements
export const validatePassword = (password: string): {
  isValid: boolean;
  error?: string;
  strength: PasswordStrength;
} => {
  const strength = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };

  if (!password) {
    return { isValid: false, error: 'Password is required', strength };
  }

  if (password.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters long', strength };
  }

  if (password.length > 128) {
    return { isValid: false, error: 'Password is too long (max 128 characters)', strength };
  }

  // Check for common weak passwords
  const commonPasswords = [
    'password', '12345678', 'qwerty123', 'abc123456', 'password123',
    'admin123', 'letmein123', 'welcome123', 'monkey123', '123456789'
  ];

  if (commonPasswords.includes(password.toLowerCase())) {
    return { isValid: false, error: 'Please choose a stronger, less common password', strength };
  }

  // Check for sequential characters
  if (/123456|abcdef|qwerty/i.test(password)) {
    return { isValid: false, error: 'Avoid using sequential characters in your password', strength };
  }

  const isStrong = Object.values(strength).every(Boolean);

  if (!isStrong) {
    const missing = [];
    if (!strength.hasUppercase) missing.push('uppercase letter');
    if (!strength.hasLowercase) missing.push('lowercase letter');
    if (!strength.hasNumber) missing.push('number');
    if (!strength.hasSpecialChar) missing.push('special character');

    return {
      isValid: false,
      error: `Password must contain: ${missing.join(', ')}`,
      strength
    };
  }

  return { isValid: true, strength };
};

// Phone number validation (supports multiple international formats)
export const validatePhone = (phone: string): { isValid: boolean; error?: string; formatted?: string } => {
  if (!phone || !phone.trim()) {
    return { isValid: false, error: 'Phone number is required' };
  }

  const trimmedPhone = phone.trim();

  // Check for alphabetic characters
  if (/[a-zA-Z]/.test(trimmedPhone)) {
    return { isValid: false, error: 'Phone number cannot contain letters. Please use numbers only.' };
  }

  // Remove all non-digit characters for validation
  const digitsOnly = trimmedPhone.replace(/\D/g, '');

  if (digitsOnly.length < 10) {
    return { isValid: false, error: 'Phone number must be at least 10 digits' };
  }

  if (digitsOnly.length > 15) {
    return { isValid: false, error: 'Phone number must be less than 15 digits' };
  }

  // Check for valid phone patterns
  const phonePatterns = [
    /^\+?1?[-.\s]?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/, // US/Canada
    /^\+?44[-.\s]?[0-9]{4}[-.\s]?[0-9]{6}$/, // UK
    /^\+?91[-.\s]?[0-9]{10}$/, // India
    /^\+?[1-9]\d{1,14}$/ // General international
  ];

  const isValidFormat = phonePatterns.some(pattern => pattern.test(trimmedPhone));

  if (!isValidFormat) {
    return {
      isValid: false,
      error: 'Please enter a valid phone number (e.g., +1-234-567-8900 or 234-567-8900)'
    };
  }

  // Format for US numbers
  if (digitsOnly.length === 10 || (digitsOnly.length === 11 && digitsOnly.startsWith('1'))) {
    const cleanDigits = digitsOnly.length === 11 ? digitsOnly.slice(1) : digitsOnly;
    const formatted = `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6)}`;
    return { isValid: true, formatted };
  }

  return { isValid: true };
};

// URL validation
export const validateUrl = (url: string, required: boolean = false): { isValid: boolean; error?: string } => {
  if (!url || !url.trim()) {
    if (required) {
      return { isValid: false, error: 'Website URL is required' };
    }
    return { isValid: true };
  }

  const trimmedUrl = url.trim();

  try {
    // Try to create a URL object
    const urlObj = new URL(trimmedUrl);

    // Check for valid protocols
    const validProtocols = ['http:', 'https:'];
    if (!validProtocols.includes(urlObj.protocol)) {
      return { isValid: false, error: 'URL must start with http:// or https://' };
    }

    // Check for valid hostname
    if (!urlObj.hostname || urlObj.hostname.length < 3) {
      return { isValid: false, error: 'Please enter a valid domain name' };
    }

    // Check for suspicious URLs
    if (urlObj.hostname.includes('localhost') || urlObj.hostname.includes('127.0.0.1')) {
      return { isValid: false, error: 'Please enter a public website URL' };
    }

    return { isValid: true };
  } catch {
    // If URL is malformed, try to help fix it
    if (!trimmedUrl.startsWith('http')) {
      return {
        isValid: false,
        error: 'URL must start with http:// or https:// (e.g., https://example.com)'
      };
    }

    return { isValid: false, error: 'Please enter a valid URL (e.g., https://example.com)' };
  }
};

// Name validation (for person names)
export const validateName = (name: string): { isValid: boolean; error?: string } => {
  if (!name || !name.trim()) {
    return { isValid: false, error: 'Name is required' };
  }

  const trimmedName = name.trim();

  if (trimmedName.length < 2) {
    return { isValid: false, error: 'Name must be at least 2 characters long' };
  }

  if (trimmedName.length > 100) {
    return { isValid: false, error: 'Name must be less than 100 characters' };
  }

  // Name can contain letters, spaces, hyphens, apostrophes, and some international characters
  const nameRegex = /^[a-zA-ZÀ-ÿĀ-žА-я\s\-'\.]+$/;
  if (!nameRegex.test(trimmedName)) {
    return {
      isValid: false,
      error: 'Name can only contain letters, spaces, hyphens, and apostrophes'
    };
  }

  // Check for suspicious patterns
  if (/^\s+|\s+$/.test(name)) {
    return { isValid: false, error: 'Name cannot start or end with spaces' };
  }

  if (/\s{2,}/.test(trimmedName)) {
    return { isValid: false, error: 'Name cannot contain multiple consecutive spaces' };
  }

  // Check for all caps (likely shouting)
  if (trimmedName === trimmedName.toUpperCase() && trimmedName.length > 5) {
    return { isValid: false, error: 'Please use proper capitalization' };
  }

  return { isValid: true };
};

// Username validation
export const validateUsername = (username: string): { isValid: boolean; error?: string } => {
  if (!username || !username.trim()) {
    return { isValid: false, error: 'Username is required' };
  }

  const trimmedUsername = username.trim();

  if (trimmedUsername.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters long' };
  }

  if (trimmedUsername.length > 30) {
    return { isValid: false, error: 'Username must be less than 30 characters' };
  }

  // Username can only contain letters, numbers, and underscores
  const usernameRegex = /^[a-zA-Z0-9_]+$/;
  if (!usernameRegex.test(trimmedUsername)) {
    return {
      isValid: false,
      error: 'Username can only contain letters, numbers, and underscores'
    };
  }

  // Username cannot start with a number
  if (/^\d/.test(trimmedUsername)) {
    return { isValid: false, error: 'Username cannot start with a number' };
  }

  // Check for reserved usernames
  const reservedUsernames = [
    'admin', 'administrator', 'root', 'user', 'test', 'guest', 'public',
    'api', 'www', 'mail', 'email', 'support', 'help', 'info', 'contact'
  ];

  if (reservedUsernames.includes(trimmedUsername.toLowerCase())) {
    return { isValid: false, error: 'This username is reserved. Please choose another one' };
  }

  return { isValid: true };
};

// Company name validation
export const validateCompany = (company: string): { isValid: boolean; error?: string } => {
  if (!company || !company.trim()) {
    return { isValid: false, error: 'Company name is required' };
  }

  const trimmedCompany = company.trim();

  if (trimmedCompany.length < 2) {
    return { isValid: false, error: 'Company name must be at least 2 characters long' };
  }

  if (trimmedCompany.length > 200) {
    return { isValid: false, error: 'Company name must be less than 200 characters' };
  }

  // Company name can contain letters, numbers, spaces, and common business punctuation
  const companyRegex = /^[a-zA-ZÀ-ÿĀ-žА-я0-9\s\-'&.,()]+$/;
  if (!companyRegex.test(trimmedCompany)) {
    return {
      isValid: false,
      error: 'Company name contains invalid characters'
    };
  }

  return { isValid: true };
};

// Job role/title validation
export const validateJobRole = (jobRole: string): { isValid: boolean; error?: string } => {
  if (!jobRole || !jobRole.trim()) {
    return { isValid: false, error: 'Job title is required' };
  }

  const trimmedJobRole = jobRole.trim();

  if (trimmedJobRole.length < 2) {
    return { isValid: false, error: 'Job title must be at least 2 characters long' };
  }

  if (trimmedJobRole.length > 100) {
    return { isValid: false, error: 'Job title must be less than 100 characters' };
  }

  // Job role can contain letters, numbers, spaces, and common punctuation
  const jobRoleRegex = /^[a-zA-ZÀ-ÿĀ-žА-я0-9\s\-'&.,()\/]+$/;
  if (!jobRoleRegex.test(trimmedJobRole)) {
    return {
      isValid: false,
      error: 'Job title contains invalid characters'
    };
  }

  return { isValid: true };
};

// Text area validation (for descriptions, messages, etc.)
export const validateTextArea = (
  text: string,
  fieldName: string,
  minLength: number = 1,
  maxLength: number = 2000,
  required: boolean = false
): { isValid: boolean; error?: string } => {
  if (!text || !text.trim()) {
    if (required) {
      return { isValid: false, error: `${fieldName} is required` };
    }
    return { isValid: true };
  }

  const trimmedText = text.trim();

  if (trimmedText.length < minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minLength} characters long`
    };
  }

  if (trimmedText.length > maxLength) {
    return {
      isValid: false,
      error: `${fieldName} must be less than ${maxLength} characters`
    };
  }

  // Check for spam-like content
  const spamPatterns = [
    /(.)\1{10,}/, // Repeated characters
    /[A-Z]{20,}/, // All caps long text
    /https?:\/\/[^\s]{10,}/g, // Long URLs
  ];

  for (const pattern of spamPatterns) {
    if (pattern.test(trimmedText)) {
      return {
        isValid: false,
        error: `${fieldName} appears to contain spam-like content`
      };
    }
  }

  return { isValid: true };
};





// Domain extraction and comparison helpers
export const extractDomain = (input: string): string | null => {
  if (!input || !input.trim()) return null;

  const trimmed = input.trim().toLowerCase();

  // Try to extract from email
  if (trimmed.includes('@')) {
    return trimmed.split('@')[1] || null;
  }

  // Try to extract from URL
  try {
    let url = trimmed;
    if (!url.startsWith('http')) {
      url = 'https://' + url;
    }
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '') || null;
  } catch {
    // If not a valid URL, try stripping generic prefixes
    return trimmed.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] || null;
  }
};

export const validateEmailDomainMatch = (email: string, website: string): { isValid: boolean; error?: string } => {
  if (!email || !website) return { isValid: true };
  const emailDomain = extractDomain(email);
  const websiteDomain = extractDomain(website);

  if (!emailDomain || !websiteDomain) return { isValid: true };

  // Compare domains
  if (emailDomain !== websiteDomain) {
    // Allow for subdomains (e.g. user@hq.company.com vs company.com)
    if (emailDomain.endsWith('.' + websiteDomain) || websiteDomain.endsWith('.' + emailDomain)) {
      return { isValid: true };
    }

    return {
      isValid: false,
      error: `Email domain (@${emailDomain}) should match company website domain (${websiteDomain})`
    };
  }

  return { isValid: true };
};

// Recipient name validation (specifically for transfers)
export const validateRecipientName = (name: string): { isValid: boolean; error?: string } => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return { isValid: false, error: VALIDATION_MESSAGES.RECIPIENT_NAME_REQUIRED };
  }
  if (trimmedName.length > 250) {
    return { isValid: false, error: VALIDATION_MESSAGES.RECIPIENT_NAME_TOO_LONG };
  }
  return { isValid: true };
};

// Recipient phone validation (specifically for transfers)
export const validateRecipientPhone = (phone: string): { isValid: boolean; error?: string } => {
  if (!phone || !phone.trim()) {
    return { isValid: false, error: VALIDATION_MESSAGES.RECIPIENT_PHONE_REQUIRED };
  }
  return { isValid: true };
};

/**
 * Validates an FDA Establishment Identifier (FEI) number.
 * Typical FEI numbers are 7 to 10 digits.
 * @param fdaFei The FEI number to validate.
 * @returns An object with isValid and an optional error message.
 */
export const validateFeiNumber = (fdaFei: string): { isValid: boolean; error?: string } => {
  if (!fdaFei || !fdaFei.trim()) {
    return { isValid: true }; // Optional field
  }

  const trimmed = fdaFei.trim();
  const digitsOnly = trimmed.replace(/\D/g, '');

  // FEI numbers are typically 7 to 10 digits
  if (digitsOnly.length < 7 || digitsOnly.length > 10 || digitsOnly !== trimmed) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.FEI_NUMBER_INVALID
    };
  }

  return { isValid: true };
};

// Export all validation functions
export default {
  validateEmail,
  validatePassword,
  validatePhone,
  validateUrl,
  validateName,
  validateUsername,
  validateCompany,
  validateJobRole,
  validateTextArea,
  extractDomain,
  validateEmailDomainMatch,
  validateRecipientName,
  validateRecipientPhone,
  validateFeiNumber,
};
